import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ContentCopy,
  Download,
  ExpandLess,
  ExpandMore,
  Publish,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';

import {
  useCommitStagedResourcesMutation,
  useGetStagedResourcesSummaryQuery,
  useImportOperationTracking,
  useListSessionPatientsQuery,
  useListStagedResourcesQuery,
} from '../store/hooks';
import { ArtifactViewerModal } from './artifact-viewer-modal';
import { PatientSelectionPanel } from './patient-selection-panel';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

/** Filename of an artifact relative to its `_artifacts/` prefix. */
const artifactName = (key: string, prefix?: string): string =>
  prefix && key.startsWith(prefix) ? key.slice(prefix.length) : key;

const STATUSES = ['staged', 'invalid', 'committed', 'skipped'] as const;
const PER_PAGE = 25;

const STATUS_COLOR: Record<
  string,
  'default' | 'info' | 'success' | 'warning' | 'error'
> = {
  staged: 'info',
  invalid: 'error',
  committed: 'success',
  skipped: 'warning',
};

interface StagedResourcesSectionProps {
  organizationId: string;
  sessionId: string;
  /** Offer the Commit action (the v2 Data Import batch detail enables it). */
  showCommit?: boolean;
}

/**
 * Displays the staged `import_resources` for an import session — a summary of
 * counts by resource type × status, plus a filterable, paginated table of the
 * staged resource envelopes (status, source key, validation errors, and the
 * converted resource data). The conversion that produces these runs outside
 * this repo. With `showCommit`, also offers the commit step: staged rows
 * become real records via an async operation polled to completion.
 */
export function StagedResourcesSection({
  organizationId,
  sessionId,
  showCommit = false,
}: StagedResourcesSectionProps) {
  const [page, setPage] = useState(1);
  const [resourceType, setResourceType] = useState('');
  const [status, setStatus] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [committing, setCommitting] = useState(false);
  // Types the operator explicitly UNchecked. Empty = commit all (the default),
  // and any new type appears selected — no init-from-async needed.
  const [deselected, setDeselected] = useState<Set<string>>(new Set());
  // Patient scope. Default 'all' commits every staged patient (no patient_keys
  // sent); 'specific' reveals the picker and commits only the checked patients
  // (inclusion model — empty until the operator chooses).
  const [patientScope, setPatientScope] = useState<'all' | 'specific'>('all');
  const [selectedPatients, setSelectedPatients] = useState<Set<string>>(
    new Set(),
  );
  const [viewerKey, setViewerKey] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const copy = useCopyToClipboard();

  const {
    data: summary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useGetStagedResourcesSummaryQuery({ organizationId, sessionId });

  const {
    data: list,
    isLoading: listLoading,
    isFetching,
    refetch: refetchList,
  } = useListStagedResourcesQuery({
    organizationId,
    sessionId,
    page,
    perPage: PER_PAGE,
    resourceType: resourceType || undefined,
    status: status || undefined,
  });

  const countsByType = summary?.counts_by_resource_type ?? {};
  const resourceTypes = Object.keys(countsByType).sort();
  const items = list?.items ?? [];
  const total = list?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const refresh = () => {
    void refetchSummary();
    void refetchList();
  };

  // The converter provenance recorded at staging time (GDN-1978): the
  // _artifacts/ prefix, the artifact object keys, and the convert.py hash.
  const artifactsPrefix = summary?.artifacts_prefix ?? undefined;
  const artifactKeys = summary?.artifact_keys ?? [];
  const converterSha256 = summary?.converter_sha256 ?? undefined;
  const hasProvenance = Boolean(
    artifactsPrefix || converterSha256 || artifactKeys.length,
  );

  // Open a single artifact via a short-lived presigned URL. The backend only
  // signs keys recorded on this session, so an arbitrary key can't be fetched.
  // Fetched fresh each time (not via cached RTK query) since the URL expires.
  const openArtifact = async (key: string) => {
    try {
      const token = await getAuth0AccessTokenSilently();
      const params = new URLSearchParams({
        organization_id: organizationId,
        key,
      });
      const res = await fetch(
        `${API_BASE_URL}/v2/admin/data-migration/sessions/${sessionId}/artifacts/download?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        throw new Error(`Download failed (${res.status})`);
      }
      const data = (await res.json()) as { url: string };
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch {
      enqueueSnackbar('Could not open artifact.', { variant: 'error' });
    }
  };

  // A type is committable in a run only if it has staged rows. Selection
  // defaults to all such types; the operator can uncheck to commit a subset
  // (e.g. patients first, then their children).
  const selectableTypes = resourceTypes.filter(
    rt => (countsByType[rt]?.staged ?? 0) > 0,
  );
  const selectedTypes = selectableTypes.filter(rt => !deselected.has(rt));
  const selectedStagedCount = selectedTypes.reduce(
    (acc, rt) => acc + (countsByType[rt]?.staged ?? 0),
    0,
  );
  const allSelected =
    selectableTypes.length > 0 &&
    selectedTypes.length === selectableTypes.length;

  const toggleType = (rt: string) =>
    setDeselected(prev => {
      const next = new Set(prev);
      if (next.has(rt)) next.delete(rt);
      else next.add(rt);
      return next;
    });

  const toggleAll = () =>
    setDeselected(allSelected ? new Set(selectableTypes) : new Set());

  // A child commits only once its patient is committed; warn if patients are
  // still staged but excluded from this selection.
  const patientsStaged = (countsByType['patient']?.staged ?? 0) > 0;
  const childSelectedWithoutPatients =
    patientsStaged &&
    !selectedTypes.includes('patient') &&
    selectedTypes.some(rt => rt !== 'patient');

  // Commit by patient: choose which patients (+ their dependency closure) to
  // commit. Defaults to all selected; a subset sends patient_keys.
  const { data: patientData, isLoading: patientsLoading } =
    useListSessionPatientsQuery(
      { organizationId, sessionId },
      { skip: !showCommit },
    );
  const patients = patientData?.items ?? [];
  const committingAllPatients = patientScope === 'all';
  const selectedPatientKeys = patients
    .map(p => p.patient_key)
    .filter(k => selectedPatients.has(k));

  // The commit count must match what actually commits. `selectedStagedCount`
  // is purely type-based and ignores the patient picker, so with a patient
  // subset it overstates the run by counting every patient's rows. Re-scope it
  // to the selected patients' closure: each staged patient row counts once
  // (the picker's per-patient `counts` excludes the patient row itself), child
  // rows come from those per-type counts. Scope 'all' sends no patient_keys, so
  // the full type-based total is exactly right.
  const isFullCommit = allSelected && committingAllPatients;
  const scopedStagedCount = committingAllPatients
    ? selectedStagedCount
    : selectedPatientKeys.reduce((runTotal, key) => {
        const summary = patients.find(p => p.patient_key === key);
        if (!summary) return runTotal;
        return (
          runTotal +
          selectedTypes.reduce((perPatient, rt) => {
            if (rt === 'patient') {
              return perPatient + (summary.patient_staged ? 1 : 0);
            }
            return perPatient + (summary.counts?.[rt] ?? 0);
          }, 0)
        );
      }, 0);

  const togglePatient = (key: string) =>
    setSelectedPatients(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // Select-all within 'specific' mode: check every patient, or clear if all are
  // already checked.
  const allPatientsChecked =
    patients.length > 0 && selectedPatientKeys.length === patients.length;
  const toggleAllPatients = () =>
    setSelectedPatients(
      allPatientsChecked ? new Set() : new Set(patients.map(p => p.patient_key)),
    );
  const changePatientScope = (scope: 'all' | 'specific') => {
    setPatientScope(scope);
    if (scope === 'all') setSelectedPatients(new Set());
  };

  const [commitStagedResources] = useCommitStagedResourcesMutation();
  const operationTracking = useImportOperationTracking({
    sessionId,
    onOperationComplete: operation => {
      setCommitting(false);
      // Reset the patient scope so the next run defaults back to committing
      // everything still staged.
      setPatientScope('all');
      setSelectedPatients(new Set());
      enqueueSnackbar(
        `Commit complete: ${operation.progress?.imported_count ?? 0} committed, ` +
          `${operation.progress?.failed_count ?? 0} failed, ` +
          `${operation.progress?.skipped_count ?? 0} skipped.`,
        { variant: 'success' },
      );
      refresh();
    },
    onOperationError: operation => {
      setCommitting(false);
      enqueueSnackbar(`Commit failed: ${operation.error ?? 'Unknown error'}`, {
        variant: 'error',
      });
      refresh();
    },
  });

  const handleCommit = async () => {
    setConfirmOpen(false);
    setCommitting(true);
    try {
      const result = await commitStagedResources({
        sessionId,
        commitStagedResourcesRequest: {
          organization_id: organizationId,
          // Send the explicit subset; all-selected == commit everything.
          resource_types: selectedTypes,
          // Scope to the chosen patients (+ their closure); omit in 'all' mode
          // so the backend commits every patient.
          patient_keys: committingAllPatients ? undefined : selectedPatientKeys,
        },
      }).unwrap();
      if (result.operation_id) {
        operationTracking.startTracking(result.operation_id);
      } else {
        setCommitting(false);
        refresh();
      }
    } catch (error: unknown) {
      setCommitting(false);
      const message =
        (error as { data?: { message?: string; error_code?: string } })?.data
          ?.message ?? 'Failed to dispatch commit.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Staged Resources"
        subheader={
          summary
            ? `Session ${summary.status} · ${total} resource(s)` +
              (summary.s3_prefix ? ` · ${summary.s3_prefix}` : '')
            : undefined
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showCommit && (
              <Button
                variant="contained"
                startIcon={
                  committing ? <CircularProgress size={16} /> : <Publish />
                }
                disabled={committing || scopedStagedCount === 0}
                onClick={() => setConfirmOpen(true)}
              >
                {committing
                  ? 'Committing…'
                  : isFullCommit
                    ? `Commit staged (${scopedStagedCount})`
                    : `Commit selected (${scopedStagedCount})`}
              </Button>
            )}
            <Tooltip title="Refresh">
              <IconButton
                onClick={refresh}
                aria-label="Refresh staged resources"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent>
        {committing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Commit running in the background — staged resources are being
            written to the live tables. This view refreshes when it finishes.
          </Alert>
        )}
        {hasProvenance && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Converter provenance
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5 }}
            >
              How this batch was converted, captured when it was staged.
            </Typography>
            {converterSha256 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: artifactsPrefix || artifactKeys.length ? 1 : 0,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 120 }}>
                  convert.py SHA-256
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    wordBreak: 'break-all',
                    flex: 1,
                  }}
                >
                  {converterSha256}
                </Typography>
                <Tooltip title="Copy SHA-256">
                  <IconButton
                    size="small"
                    aria-label="Copy converter SHA-256"
                    onClick={() => copy(converterSha256, 'SHA-256')}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {artifactsPrefix && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: artifactKeys.length ? 1 : 0,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 120 }}>
                  Artifacts prefix
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    wordBreak: 'break-all',
                    flex: 1,
                  }}
                >
                  {artifactsPrefix}
                </Typography>
                <Tooltip title="Copy prefix">
                  <IconButton
                    size="small"
                    aria-label="Copy artifacts prefix"
                    onClick={() => copy(artifactsPrefix, 'Prefix')}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {artifactKeys.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Artifacts
                </Typography>
                {artifactKeys.map((key) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.25,
                    }}
                  >
                    <Typography
                      variant="body2"
                      onClick={() => setViewerKey(key)}
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        wordBreak: 'break-all',
                        flex: 1,
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {artifactName(key, artifactsPrefix)}
                    </Typography>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        aria-label={`View artifact ${key}`}
                        onClick={() => setViewerKey(key)}
                      >
                        <Visibility fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        aria-label={`Download artifact ${key}`}
                        onClick={() => void openArtifact(key)}
                      >
                        <Download fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
        {summaryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : resourceTypes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No staged resources for this session yet.
          </Typography>
        ) : (
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                {showCommit && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={selectedTypes.length > 0 && !allSelected}
                      disabled={selectableTypes.length === 0}
                      onChange={toggleAll}
                      slotProps={{
                        input: { 'aria-label': 'Select all types to commit' },
                      }}
                    />
                  </TableCell>
                )}
                <TableCell>Resource type</TableCell>
                {STATUSES.map(s => (
                  <TableCell key={s} align="right">
                    {s}
                  </TableCell>
                ))}
                <TableCell align="right">total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resourceTypes.map(rt => {
                const row = countsByType[rt] ?? {};
                const rowTotal = Object.values(row).reduce((a, b) => a + b, 0);
                const committable = (row.staged ?? 0) > 0;
                return (
                  <TableRow
                    key={rt}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setResourceType(rt);
                      setPage(1);
                    }}
                  >
                    {showCommit && (
                      <TableCell
                        padding="checkbox"
                        onClick={e => e.stopPropagation()}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedTypes.includes(rt)}
                          disabled={!committable}
                          onChange={() => toggleType(rt)}
                          slotProps={{ input: { 'aria-label': `Commit ${rt}` } }}
                        />
                      </TableCell>
                    )}
                    <TableCell>{rt}</TableCell>
                    {STATUSES.map(s => (
                      <TableCell key={s} align="right">
                        {row[s] ?? 0}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <strong>{rowTotal}</strong>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {showCommit && childSelectedWithoutPatients && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Some selected types depend on patients, but patients are still
            staged and not selected. Commit patients first, or include them —
            otherwise their children will block as unresolved.
          </Alert>
        )}

        {showCommit && (
          <PatientSelectionPanel
            patients={patients}
            scope={patientScope}
            onScopeChange={changePatientScope}
            selected={selectedPatients}
            allChecked={allPatientsChecked}
            onToggle={togglePatient}
            onToggleAll={toggleAllPatients}
            isLoading={patientsLoading}
          />
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Resource type</InputLabel>
            <Select
              label="Resource type"
              value={resourceType}
              onChange={e => {
                setResourceType(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {resourceTypes.map(rt => (
                <MenuItem key={rt} value={rt}>
                  {rt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={e => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isFetching && <CircularProgress size={18} />}
        </Box>

        {listLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No resources match the current filters.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Resource type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Committed ID</TableCell>
                <TableCell>Source key</TableCell>
                <TableCell align="right">Errors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => {
                const isOpen = expanded === item.import_resource_id;
                const errs = item.validation_errors ?? [];
                const commitErrs = item.commit_errors ?? [];
                return (
                  <Fragment key={item.import_resource_id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label="Toggle details"
                          onClick={() =>
                            setExpanded(
                              isOpen ? null : item.import_resource_id,
                            )
                          }
                        >
                          {isOpen ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{item.resource_type}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.status}
                          color={STATUS_COLOR[item.status] ?? 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {item.committed_resource_id ? (
                          <Link
                            to={`/admin-v2/resources/${item.resource_type}/${item.committed_resource_id}?organization_id=${organizationId}`}
                          >
                            {item.committed_resource_id}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          wordBreak: 'break-all',
                        }}
                      >
                        {item.source_key}
                      </TableCell>
                      <TableCell align="right">
                        {errs.length > 0 || commitErrs.length > 0 ? (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              justifyContent: 'flex-end',
                            }}
                          >
                            {errs.length > 0 && (
                              <Chip
                                size="small"
                                color="error"
                                label={`${errs.length} validation`}
                              />
                            )}
                            {commitErrs.length > 0 && (
                              <Chip
                                size="small"
                                color="warning"
                                label={`${commitErrs.length} commit`}
                              />
                            )}
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ py: 0, border: isOpen ? undefined : 0 }}
                      >
                        <Collapse in={isOpen} unmountOnExit>
                          <Box sx={{ p: 2 }}>
                            {errs.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="error">
                                  Validation errors
                                </Typography>
                                <pre
                                  style={{
                                    margin: 0,
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {JSON.stringify(errs, null, 2)}
                                </pre>
                              </Box>
                            )}
                            {commitErrs.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="warning.main"
                                >
                                  Commit errors
                                </Typography>
                                <pre
                                  style={{
                                    margin: 0,
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {JSON.stringify(commitErrs, null, 2)}
                                </pre>
                              </Box>
                            )}
                            <Typography variant="subtitle2">Data</Typography>
                            <pre
                              style={{
                                margin: 0,
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                                maxHeight: 320,
                                overflow: 'auto',
                              }}
                            >
                              {JSON.stringify(item.data ?? {}, null, 2)}
                            </pre>
                            {item.refs &&
                              Object.keys(item.refs).length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2">
                                    Refs
                                  </Typography>
                                  <pre style={{ margin: 0, fontSize: 12 }}>
                                    {JSON.stringify(item.refs, null, 2)}
                                  </pre>
                                </Box>
                              )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_e, p) => setPage(p)}
              size="small"
            />
          </Box>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {isFullCommit ? 'Commit staged resources?' : 'Commit selected?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This writes {scopedStagedCount} staged resource
            {scopedStagedCount === 1 ? '' : 's'}
            {allSelected ? '' : ` (${selectedTypes.join(', ')})`}
            {committingAllPatients
              ? ''
              : ` for ${selectedPatientKeys.length} selected patient${
                  selectedPatientKeys.length === 1 ? '' : 's'
                }`}{' '}
            into the live tables for this organization. Rows that fail are
            marked with commit errors and the run continues; already-committed
            rows are skipped, so re-running is safe.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleCommit()}>
            Commit
          </Button>
        </DialogActions>
      </Dialog>

      <ArtifactViewerModal
        open={viewerKey !== null}
        onClose={() => setViewerKey(null)}
        organizationId={organizationId}
        sessionId={sessionId}
        artifactKey={viewerKey}
        displayName={
          viewerKey ? artifactName(viewerKey, artifactsPrefix) : ''
        }
        onDownload={(key) => void openArtifact(key)}
      />
    </Card>
  );
}
